import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "../AddWorkReport/work_report.css";
import DataTable from "../../components/custom_data_table";
import {
  getReport,
  getAllAssignedProjects,
} from "../../service/project_service";
import CustomLoadingindicator from "../../components/custom_loading";
import { CustomSearchableSelect } from "../../components/admin/AdminFields";
import { Eye, X, Layers, Calendar, Clock, ClipboardList } from "lucide-react";

const MyReports = () => {
  const [projects, setProjects] = useState([]);
  const [workReports, setWorkReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [projectid, setProjectId] = useState(0);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInitialData = async () => {
    try {
      const projectsRes = await getAllAssignedProjects();
      if (projectsRes?.status === "success") {
        setProjects(projectsRes.data || []);
      }
      await fetchReports("", "", 0);
    } catch (err) {
      toast.error(err?.message || "Failed to load projects");
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchReports = async (sDate, eDate, pid) => {
    setLoading(true);
    try {
      const reportsRes = await getReport({
        startDate: sDate,
        endDate: eDate,
        projectid: pid,
      });

      if (reportsRes?.status === "success") {
        const reports = reportsRes.data || [];
        const formattedReports = reports.map((report, index) => ({
          sno: index + 1,
          reportid: report.reportid,
          projectName: report.projectname || "N/A",
          moduleName: report.modulename || "N/A",
          projectTaskName: report.projecttaskname || "-",
          taskDescription: report.taskname || "-",
          workDate: report.workdate || "",
          startTime: report.starttime || "-",
          endTime: report.endtime || "-",
          duration: (() => {
            const val = Number(report.workinghours) || 0;
            const h = Math.floor(val);
            const m = Math.round((val - h) * 100);
            if (h === 0 && m === 0) return "0 hrs";
            let str = "";
            if (h > 0) str += `${h} hr${h > 1 ? "s" : ""} `;
            if (m > 0) str += `${m} min${m > 1 ? "s" : ""}`;
            return str.trim();
          })(),
        }));

        setWorkReports(formattedReports);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      toast.error("End date cannot be earlier than start date");
      return;
    }
    fetchReports(startDate, endDate, projectid);
  };

  const filteredReports = workReports;

  const handleResetFilters = (e) => {
    e.preventDefault();
    setProjectId(0);
    setStartDate("");
    setEndDate("");
    fetchReports("", "", 0);
  };

  const reportColumns = [
    { key: "sno", label: "S.No" },
    {
      key: "actions",
      label: "View",
      render: (_, row) => (
        <button
          onClick={() => {
            setViewData(row);
            setShowViewModal(true);
          }}
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="View Details"
        >
          <Eye size={18} color="#2563eb" />
        </button>
      ),
    },
    { key: "projectName", label: "Project" },
    { key: "moduleName", label: "Module" },
    { key: "projectTaskName", label: "Assigned Task" },
    {
      key: "taskDescription",
      label: "Work Done",
      render: (val) => <div style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{val}</div>
    },
    {
      key: "workDate",
      label: "Work Date",
      render: (value) =>
        value ? new Date(value).toLocaleDateString("en-GB") : "-",
    },
    { key: "startTime", label: "Start" },
    { key: "endTime", label: "End" },
    { key: "duration", label: "Duration" },
  ];

  if (initialLoading) {
    return <CustomLoadingindicator />;
  }

  return (
    <div className="add-report-container">
      <h4>My Work Reports</h4>

      <div className="add-report-card">
        <form className="add-report-form" onSubmit={handleSearch}>
          <div className="report-form-group">
            <CustomSearchableSelect
              label="Project"
              placeholder="All Projects"
              value={projectid}
              onChange={(e) => setProjectId(e.target.value)}
              options={projects.map((item) => ({
                label: item.projectname,
                value: item.projectid,
              }))}
            />
          </div>

          <div className="report-form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="report-form-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </form>

        <div className="submit-buttons" style={{ marginTop: "20px" }}>
          <button
            className="apply-btn"
            onClick={handleSearch}
            disabled={loading}
            style={{ cursor: loading ? "not-allowed" : "pointer" }}
          >
            {loading ? "Searching..." : "Search"}
          </button>
          <button
            className="reset-btn"
            onClick={handleResetFilters}
            disabled={loading}
            style={{ cursor: loading ? "not-allowed" : "pointer" }}
          >
            Reset
          </button>
        </div>
      </div>

      <div style={{ height: 20 }}></div>

      <DataTable
        columns={reportColumns}
        data={filteredReports}
        loading={loading}
        pageSize={10}
      />

      {/* Premium View Detail Modal */}
      {showViewModal && viewData && (
        <div
          className="premium-dialog-overlay"
          onClick={() => setShowViewModal(false)}
        >
          <div
            className="premium-view-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header-premium">
              <div className="header-title-group">
                <div className="header-icon-badge">
                  <ClipboardList size={20} />
                </div>
                <h5>Work Report Details</h5>
              </div>
              <button
                className="modal-close-circle"
                onClick={() => setShowViewModal(false)}
              >
                <X size={18} />
              </button>
            </div>

            <div className="modal-body-scrollable">
              <div className="detail-section-card">
                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <Layers size={14} />
                    <span>Project & Module</span>
                  </div>
                  <div className="detail-value-text">
                    {viewData.projectName} - {viewData.moduleName}
                  </div>
                </div>

                <div className="detail-grid-layout">
                  <div className="detail-row-item">
                    <div className="detail-label-icon">
                      <Calendar size={14} />
                      <span>Work Date</span>
                    </div>
                    <div className="detail-value-text">
                      {viewData.workDate
                        ? new Date(viewData.workDate).toLocaleDateString(
                          "en-GB",
                        )
                        : "-"}
                    </div>
                  </div>
                  <div className="detail-row-item">
                    <div className="detail-label-icon">
                      <Clock size={14} />
                      <span>Total Duration</span>
                    </div>
                    <div className="detail-value-text">{viewData.duration}</div>
                  </div>
                </div>

                <div className="detail-grid-layout" style={{ borderTop: "none" }}>
                  <div className="detail-row-item">
                    <div className="detail-label-icon">
                      <Clock size={14} />
                      <span>Start Time</span>
                    </div>
                    <div className="detail-value-text">{viewData.startTime}</div>
                  </div>
                  <div className="detail-row-item">
                    <div className="detail-label-icon">
                      <Clock size={14} />
                      <span>End Time</span>
                    </div>
                    <div className="detail-value-text">{viewData.endTime}</div>
                  </div>
                </div>

                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <ClipboardList size={14} />
                    <span>Assigned Task</span>
                  </div>
                  <div className="detail-value-text">{viewData.projectTaskName}</div>
                </div>

                <div className="detail-row-item">
                  <div className="detail-label-icon">
                    <ClipboardList size={14} />
                    <span>Detailed Work Notes</span>
                  </div>
                  <div className="remark-surface-box">{viewData.taskDescription}</div>
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <button
                className="modal-action-btn"
                onClick={() => setShowViewModal(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyReports;
